/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2022 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * Device Account Warning component
 *
 * @file AccountWarning.jsx
 * @author Infant Diana Sebastian
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@greenville/mui-theme';
import Framework from '@greenville/framework';

import * as constants from '../constants';
import CommonUtils from '../utils/CommonUtils';
import env from '../env';

const DialogPaper = styled('div')({
  '&&': {
    minWidth: 600,
    height: 418,
    outline: 'none',

    '@media screen and (max-width: 700px)': {
      width: '90%',
      minWidth: 250,
      height: 525
    }
  }
});

const WarningDialogAction = styled(DialogActions)({
  '&&': {
    margin: 0,
    padding: 8
  }
});

const WarningInfo = styled('div')({
  '&&': {
    margin: 8
  }
});

const WarningDialogTitle = styled(DialogTitle)({
  '&&': {
    textAlign: 'center',
    width: '100%',
    padding: 0,
    paddingBottom: 8
  }
});

const WarningDialogHeader = styled(Typography)({
  '&&': {
    fontFamily: 'TT Commons',
    fontSize: 28,
    lineHeight: '110%',
    padding: '0.5px',
    fontWeight: 'bold'
  }
});

const WarningDialogContent = styled(DialogContent)({
  '&&': {
    padding: 0,
    textAlign: 'center',
    '@media screen and (max-width: 700px)': {
      padding: '0 20px'
    }
  }
});

const WarningDialogContentHeader = styled(Typography)({
  '&&': {
    fontFamily: 'Hind',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 16,
    lineHeight: '150%',
    color: '#333333'
  }
});

const WarningDialogActions = styled(DialogActions)({
  '&&': {
    justifyContent: 'center',
    padding: 0,
    margin: 24,

    '@media screen and (max-width: 700px)': {
      display: 'grid'
    }
  }
});

const LaterAction = styled(Button)({
  '&&': {
    background: '#FEFEFE',
    border: '1px solid #E8EEFA',
    borderRadius: 40,
    padding: '7px 10.5px',
    minWidth: 'unset',

    '&:hover': {
      borderColor: '#252525'
    },

    '@media screen and (max-width: 700px)': {
      marginBottom: 16
    }
  }
});

const LaterActionText = styled(Typography)({
  '&&': {
    fontFamily: 'Hind',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: 16,
    width: '87px',
    lineHeight: '150%',
    color: '#333333',
    minWidth: '104px'
  }
});

const ChangePwdAction = styled(Button)({
  '&&': {
    marginLeft: '16px !important',
    background: 'linear-gradient(135deg, #EE512E 0%, #DF006B 63.44%, #BD0071 100%)',
    borderRadius: 40,
    border: 'none',
    padding: '8px 20px',
    '&:hover': {
      background: 'linear-gradient(135deg, #D6492A 0%, #BA015A 63.44%, #940159 100%)'
    }
  }
});

const AccountWarning = ({
  open, onClose
}) => {
  const language = Framework.getStoreRegistry().getStore('language');
  const handlepwdChange = () => {
    CommonUtils.dispatchGaEvent(constants.CONCURRENT_SESSIONS_MODAL_EVENT,
      constants.CONCURRENT_SESSIONS_EVENT_NAME, constants.RESPOND_TO_WARNING, constants.PASSWORD_EVENT_LABEL);
    window.open(env.BOOKSHELF_CONFIG.changePassword, '_self');
  };
  const handleOnClose = (event, dismiss = false) => {
    const eventLabel = dismiss ? constants.DISMISS_EVENT_LABEL : constants.LATER_EVENT_LABEL;
    CommonUtils.dispatchGaEvent(constants.CONCURRENT_SESSIONS_MODAL_EVENT,
      constants.CONCURRENT_SESSIONS_EVENT_NAME, constants.RESPOND_TO_WARNING, eventLabel);
    onClose(event);
  };
  return (
    <Dialog
      open={open}
      id="account-warning-dialog"
      onClose={(event) => { handleOnClose(event, true); }}
      maxWidth="sm"
      slotProps={{
        paper: {
          component: DialogPaper
        }
      }}
      aria-labelledby="account-warning-dialog-title"
      aria-describedby="account-warning-dialog-description"
      role="dialog"
      aria-modal="true"
      className="dialog-zindex"
    >
      <WarningDialogAction>
        <IconButton
          id="remove-button"
          aria-label="Close"
          onClick={(event) => { handleOnClose(event, true); }}
        >
          <Icon id="remove-icon" name="Remove" color="action" fontSize="large" />
        </IconButton>
      </WarningDialogAction>
      <WarningInfo>
        <WarningDialogTitle
          disableTypography
          id="account-warning-dialog-title"
        >
          <WarningDialogHeader variant="h3" component="h2">
            <FormattedMessage {...language.getText('device.THREATONE_TITLE')} />
          </WarningDialogHeader>
        </WarningDialogTitle>
      </WarningInfo>
      <WarningDialogContent id="account-warning-dialog-description">
        <WarningDialogContentHeader variant="subtitle2" component="p">
          <FormattedMessage {...language.getText('device.THREATONE_DESC')} />
        </WarningDialogContentHeader>
        <WarningDialogContentHeader variant="subtitle2" component="p">
          <FormattedMessage {...language.getText('device.THREATONE_DESC_TWO')} />
        </WarningDialogContentHeader>
        <WarningDialogActions>
          <LaterAction
            color="default"
            variant="outlined"
            id="close"
            onClick={handleOnClose}
          >
            <LaterActionText variant="body2">
              <FormattedMessage {...language.getText('device.MAY_BE_LATER')} />
            </LaterActionText>
          </LaterAction>
          <ChangePwdAction
            color="default"
            variant="outlined"
            id="change-password"
            onClick={handlepwdChange}
          >
            <LaterActionText variant="body2" style={{ color: '#F7F9FD', width: '129px' }}>
              <FormattedMessage {...language.getText('device.ACCESS_CHANGE_PASSWORD')} />
            </LaterActionText>
          </ChangePwdAction>
        </WarningDialogActions>
      </WarningDialogContent>
    </Dialog>
  );
};

AccountWarning.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AccountWarning;
